import { User } from "@supabase/supabase-js";
import { supabase } from "../../../supabaseClient";
import { getGeminiEmbedding } from "../../ai/GeminiUtils";
import { logAnalyticsEvent } from "../../engagement/Amplitude";
import { addPoints } from "../../user/utils/UserUtils";
import { Task } from "../models/Task";
import { QueryClient } from "react-query";

export async function editTask({
  task,
  title,
  description,
  snippet,
  tags,
  user,
  setSubmitting,
}: {
  task: Task;
  title: string;
  description: string;
  snippet: string;
  tags: string[];
  user: User;
  setSubmitting: (value: boolean) => void;
}): Promise<Task | undefined> {
  setSubmitting(true);
  const embedding = await getGeminiEmbedding(title);
  const { data, error } = await supabase
    .from("tasks")
    .update([
      {
        title,
        description,
        tags,
        embedding: embedding,
        snippet,
        edit_count: (task?.edit_count ?? 0) + 1,
        updated_at: new Date().toISOString(),
        updated_by: user?.id,
      },
    ])
    .eq("id", task.id)
    .select("*");

  await supabase.from("task_history").insert({
    task_id: task.id,
    title: task.title,
    description: task.description,
    tags: task.tags,
    user_id: task.user_id,
    updated_by: task?.updated_by,
    updated_at: task.updated_at,
    vote_total: task.vote_total,
    edit_count: task.edit_count,
    created_at: new Date().toISOString(),
  });

  if (error) {
    logAnalyticsEvent("Error Submitting Guide", {
      method: "edit",
    });
    setSubmitting(false);
    console.error("Error submitting task:", error);
    throw error;
  } else {
    logAnalyticsEvent("Task Submitted", {
      method: "edit",
    });
    addPoints({
      points: 1,
      userId: user?.id,
      reason: "Edit Task",
      taskId: data![0].id,
    });
    setSubmitting(false);
    return data![0];
  }
}

export const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
};

export const recordVote = async ({
  taskId,
  value,
  task,
  user,
  showTempMessage,
  showTempErrorMessage,
  queryClient,
}: {
  taskId: number;
  value: number;
  task: Task;
  user: User;
  showTempMessage: (message: string) => void;
  showTempErrorMessage: (message: string) => void;
  queryClient: QueryClient;
}) => {
  if (
    ((task?.task_votes ?? []).filter((vote) => vote.user_id === user?.id)
      .length ?? 0) > 0
  ) {
    showTempMessage("You can only vote once");

    return;
  }

  if (user === null || user === undefined) {
    showTempErrorMessage("You must be logged in to vote");

    return;
  }

  logAnalyticsEvent("Vote", { taskId: taskId, value: value });

  const updatedTask = task;
  if (updatedTask) {
    if (updatedTask.task_votes === null) {
      updatedTask.task_votes = [];
    }

    updatedTask.task_votes!.push({
      value: value,
      user_id: user?.id,
      task_id: taskId,
      created_at: new Date().toISOString(),
    });

    updatedTask.vote_total = updatedTask.task_votes!.length;
  }

  queryClient.setQueryData(["task", task.id], task);

  try {
    const { data, error } = await supabase
      .from("task_votes")
      .insert({ task_id: taskId, user_id: user?.id, value: value });

    if (value === 1 && task?.user_id !== user?.id) {
      addPoints({
        points: 10,
        userId: task?.user_id,
        reason: "task Upvoted",
        taskId: task?.id,
      });
    }

    // Increment the voteTotal on the task
    await supabase
      .from("tasks")
      .update({ vote_total: task?.vote_total ?? 0 })
      .eq("id", taskId);

    if (error) {
      console.error("Error recording vote:", error);
    }
  } catch (error) {
    console.error("Error recording vote:", error);
  }
};

export const handleShare = ({
  task,
  user,
  showTempMessage,
}: {
  task: Task;
  user: User;
  showTempMessage: (message: string) => void;
}) => {
  logAnalyticsEvent("Share Task");
  let currentUrl = window.location.href;

  if (currentUrl.includes("http://localhost:3000")) {
    currentUrl = currentUrl.replace(
      "http://localhost:3000",
      "https://cotr.dev"
    );
  }

  navigator.clipboard.writeText(currentUrl);
  showTempMessage("Copied to clipboard!");
};

export const handleSave = async ({
  task,
  user,
  showTempMessage,
  queryClient,
  id,
}: {
  task: Task;
  user: User;
  showTempMessage: (message: string) => void;
  queryClient: QueryClient;
  id: number;
}) => {
  console.log("id", id);
  try {
    if (task?.user_saves?.includes(user?.id ?? "")) {
      console.log("Unsaving task");
      logAnalyticsEvent("Unsave Snippet", {
        taskId: task?.id,
      });
      const { data, error } = await supabase
        .from("tasks")
        .update({
          user_saves: task?.user_saves?.filter((userId) => userId !== user?.id),
        })
        .eq("id", task?.id)
        .select("*");

      // queryClient.invalidateQueries(["snippet", id]);

      queryClient.setQueryData(["snippet", id.toString()], data![0]);

      showTempMessage("Snippet Unsaved!");
    } else {
      console.log("Saving task");
      logAnalyticsEvent("Save Snippet", {
        taskId: task?.id,
      });
      const { data, error } = await supabase
        .from("tasks")
        .update({
          user_saves: task?.user_saves
            ? [...task?.user_saves, user?.id]
            : [user?.id],
        })
        .eq("id", task?.id)
        .select("*");

      // queryClient.invalidateQueries(["snippet", id]);

      queryClient.setQueryData(["snippet", id.toString()], data![0]);

      showTempMessage("Snippet Saved!");
    }
  } catch (error) {
    console.error("Error saving task:", error);
  }
};
